// The game's screens (RALLY_GOAL.md §10, §13, §16): main menu over the car showcase, settings,
// the controls card, credits, pause, finish, and the touch-device notice.
import manifest from '../../assets.manifest.json';
import type { Settings, AssistPreset } from '../game/settings';
import { assistsFor, presetOf } from '../game/settings';
import type { BestEntry, StageResult } from '../game/timing';
import { formatDelta, formatTime, RESET_PENALTY } from '../game/timing';
import type { PresetName } from '../render/quality';
import { PRESETS } from '../render/quality';
import type { CameraMode } from '../render/cameras';
import { adjustable, button, h, refreshAll, slider, stepper, toggle, type Screen, type Screens } from './screens';
import { DEFAULT_KEYS, STANDARD_PAD, rebindKey, type Action, type PadBinding, type PadMapping } from '../input/devices';
import { bindingLabel, keyLabel, type CaptureKind, type CaptureResult } from '../input/capture';

export interface MenuContext {
  title: string;
  tagline: string;
  stageName: string;
  stageKm: number;
  carName: string;
  carLine: string;
  cars: { id: string; name: string }[];
  stages: { id: string; name: string }[];
  carId: string;
  stageId: string;
  /** Switch car or stage (reloads into the new session). */
  selectContent(kind: 'car' | 'stage', id: string): void;
  settings: Settings;
  detectedPreset: PresetName;
  best(): BestEntry | null;
  /** Persist and apply the settings after a change. */
  changed(): void;
  drive(): void;
  restart(): void;
  resetToRoad(): void;
  resume(): void;
  quitToMenu(): void;
  fullscreen(): void;
  /** Stage time for the pause screen. */
  stageTime(): string;
  /** Remapping: the next key (null = cancelled), and a controller capture (null = timed out). */
  captureKey(cb: (code: string | null) => void): void;
  capturePad(kind: CaptureKind, cb: (r: CaptureResult | null) => void): void;
}

function fullscreenButton(ctx: MenuContext, parent: HTMLElement): HTMLButtonElement {
  const b = button(document.fullscreenElement ? 'EXIT FULL SCREEN' : 'FULL SCREEN', () => {
    ctx.fullscreen();
    setTimeout(() => (b.firstElementChild!.textContent = document.fullscreenElement ? 'EXIT FULL SCREEN' : 'FULL SCREEN'), 200);
  }, 'btn-small fs', parent);
  return b;
}

const pct = (v: number) => `${Math.round(v * 100)}%`;

export function mainMenu(ctx: MenuContext, screens: Screens): Screen {
  const el = h('div', 'menu-main');
  const side = h('div', 'menu-side', undefined, el);
  const brand = h('div', 'brand', undefined, side);
  h('span', 'brand-mark', undefined, brand);
  h('span', 'brand-name', ctx.title, brand);
  h('div', 'menu-tagline', ctx.tagline, side);
  const nav = h('div', 'menu-nav', undefined, side);
  const drive = button('DRIVE', () => ctx.drive(), 'btn-primary', nav);
  drive.id = 'btn-drive';
  button('SETTINGS', () => screens.push(settingsScreen(ctx, screens)), '', nav);
  button('CONTROLS', () => screens.push(controlsScreen(ctx, screens, 'BACK', () => screens.pop())), '', nav);
  button('CREDITS', () => screens.push(creditsScreen(screens)), '', nav);
  // Stage and car card.
  const card = h('div', 'menu-card', undefined, el);
  const st = h('div', 'card-block', undefined, card);
  h('div', 'card-kicker', 'SPECIAL STAGE', st);
  contentTitle(st, ctx, 'stage', ctx.stages, ctx.stageId, ctx.stageName);
  h('div', 'card-line', `${ctx.stageKm.toFixed(2)} km · gravel, broken tarmac, mud, a ford`, st);
  const best = ctx.best();
  h('div', 'card-best', best ? `PERSONAL BEST  ${formatTime(best.total)}` : 'NO TIME SET YET', st);
  const car = h('div', 'card-block', undefined, card);
  h('div', 'card-kicker', 'CAR', car);
  contentTitle(car, ctx, 'car', ctx.cars, ctx.carId, ctx.carName);
  h('div', 'card-line', ctx.carLine, car);
  h('div', 'menu-hint', 'Using a controller? Press any button on it.', el);
  fullscreenButton(ctx, el);
  return { el, initial: drive, onBack: () => undefined, onStart: () => ctx.drive() };
}

/** Card title, or a selector when more than one car/stage is available (new content folders appear here). */
function contentTitle(parent: HTMLElement, ctx: MenuContext, kind: 'car' | 'stage', list: { id: string; name: string }[], current: string, name: string): void {
  if (list.length < 2) {
    h('div', 'card-title', name, parent);
    return;
  }
  const b = h('button', 'card-title card-pick', undefined, parent);
  b.type = 'button';
  const i = Math.max(0, list.findIndex((x) => x.id === current));
  b.textContent = `‹ ${list[i].name} ›`;
  const go = (d: -1 | 1) => ctx.selectContent(kind, list[(i + d + list.length) % list.length].id);
  adjustable(b, go);
  b.addEventListener('click', () => go(1));
}

function tabs(parent: HTMLElement, names: string[], onSelect: (i: number) => void): { select: (i: number) => void; el: HTMLElement } {
  const bar = h('div', 'tabs', undefined, parent);
  bar.setAttribute('role', 'tablist');
  let cur = 0;
  const items = names.map((n, i) => {
    const t = h('span', 'tab', n, bar);
    t.addEventListener('click', () => select(i));
    return t;
  });
  const select = (i: number) => {
    cur = (i + names.length) % names.length;
    items.forEach((t, j) => t.classList.toggle('on', j === cur));
    onSelect(cur);
  };
  adjustable(bar, (d) => select(cur + d));
  select(0);
  return { select, el: bar };
}

export function settingsScreen(ctx: MenuContext, screens: Screens): Screen {
  const s = ctx.settings;
  const el = h('div', 'menu-panel settings');
  const head = h('div', 'panel-head', undefined, el);
  h('h1', '', 'SETTINGS', head);
  h('div', 'panel-sub', '← → change · ↑ ↓ move · Esc back', head);
  const pages: HTMLElement[] = [];
  const body = h('div', 'panel-body');
  const bar = tabs(el, ['GRAPHICS', 'VIEW', 'AUDIO', 'CO-DRIVER', 'DRIVING', 'INPUT', 'HUD'], (i) => {
    pages.forEach((p, j) => p.classList.toggle('hidden', j !== i));
  });
  el.appendChild(body);
  const page = () => {
    const p = h('div', 'page', undefined, body);
    pages.push(p);
    return p;
  };
  const ch = () => ctx.changed();

  // Graphics.
  const g = page();
  const presetBase = () => PRESETS[s.graphics.preset === 'auto' ? ctx.detectedPreset : s.graphics.preset];
  stepper<Settings['graphics']['preset']>(
    g,
    'Preset',
    [
      { value: 'auto', label: `AUTO (${ctx.detectedPreset.toUpperCase()})` },
      { value: 'low', label: 'LOW' },
      { value: 'medium', label: 'MEDIUM' },
      { value: 'high', label: 'HIGH' },
      { value: 'ultra', label: 'ULTRA' },
    ],
    () => s.graphics.preset,
    (v) => {
      s.graphics = { preset: v, renderScale: null, shadows: null, vegetation: null, post: null, motionBlur: null, dof: null };
      ch();
      refreshAll(g);
    },
  );
  slider(g, 'Render scale', 0.5, 1, 0.05, () => s.graphics.renderScale ?? presetBase().renderScale, (v) => ((s.graphics.renderScale = v), ch()), pct);
  stepper(
    g,
    'Shadows',
    [
      { value: 0, label: 'OFF' },
      { value: 1, label: 'LOW' },
      { value: 2, label: 'MEDIUM' },
      { value: 3, label: 'HIGH' },
    ],
    () => s.graphics.shadows ?? presetBase().shadowCascades,
    (v) => ((s.graphics.shadows = v), ch()),
  );
  slider(g, 'Vegetation distance', 0.4, 1.3, 0.05, () => s.graphics.vegetation ?? presetBase().vegetation, (v) => ((s.graphics.vegetation = v), ch()), pct);
  toggle(g, 'Ambient occlusion', () => s.graphics.post ?? presetBase().ao, (v) => ((s.graphics.post = v), ch()));
  toggle(g, 'Motion blur', () => s.graphics.motionBlur ?? presetBase().motionBlur, (v) => ((s.graphics.motionBlur = v), ch()));
  toggle(g, 'Depth of field (menu showcase)', () => s.graphics.dof ?? presetBase().dof, (v) => ((s.graphics.dof = v), ch()));
  h('div', 'row-note', 'Dynamic resolution keeps the frame rate up by lowering the render scale when needed.', g);

  // View.
  const v = page();
  const cams: { value: CameraMode; label: string }[] = [
    { value: 'chase-near', label: 'CHASE NEAR' },
    { value: 'chase-far', label: 'CHASE FAR' },
    { value: 'cockpit', label: 'COCKPIT' },
    { value: 'bonnet', label: 'BONNET' },
    { value: 'bumper', label: 'BUMPER' },
  ];
  stepper(v, 'Camera', cams, () => s.camera, (x) => ((s.camera = x), ch()));
  for (const c of cams) slider(v, `Field of view: ${c.label.toLowerCase()}`, 40, 100, 1, () => s.fov[c.value], (x) => ((s.fov[c.value] = x), ch()), (x) => `${Math.round(x)}°`);
  toggle(v, 'Reduce motion', () => s.reduceMotion, (x) => ((s.reduceMotion = x), ch()));
  stepper<Settings['units']>(
    v,
    'Units',
    [
      { value: 'kmh', label: 'KM/H' },
      { value: 'mph', label: 'MPH' },
    ],
    () => s.units,
    (x) => ((s.units = x), ch()),
  );

  // Audio.
  const a = page();
  slider(a, 'Master', 0, 1, 0.05, () => s.volumes.master, (x) => ((s.volumes.master = x), ch()), pct);
  slider(a, 'Engine', 0, 1, 0.05, () => s.volumes.engine, (x) => ((s.volumes.engine = x), ch()), pct);
  slider(a, 'Effects and ambience', 0, 1, 0.05, () => s.volumes.effects, (x) => ((s.volumes.effects = x), ch()), pct);
  slider(a, 'Co-driver', 0, 1, 0.05, () => s.volumes.codriver, (x) => ((s.volumes.codriver = x), ch()), pct);

  // Co-driver.
  const c = page();
  toggle(c, 'Voice', () => s.codriver.voice, (x) => ((s.codriver.voice = x), ch()));
  stepper<Settings['codriver']['timing']>(
    c,
    'Call timing',
    [
      { value: 'early', label: 'EARLY' },
      { value: 'normal', label: 'NORMAL' },
      { value: 'late', label: 'LATE' },
    ],
    () => s.codriver.timing,
    (x) => ((s.codriver.timing = x), ch()),
  );
  toggle(c, 'Pace-note symbols', () => s.codriver.symbols, (x) => ((s.codriver.symbols = x), ch()));
  toggle(c, 'Subtitles', () => s.codriver.subtitles, (x) => ((s.codriver.subtitles = x), ch()));

  // Driving.
  const d = page();
  const presetRow = assistPresetStepper(d, ctx, () => refreshAll(d));
  void presetRow;
  const assist = (label: string, key: keyof Settings['assists'], on = 'ON', off = 'OFF') =>
    stepper(
      d,
      label,
      [
        { value: false, label: off },
        { value: true, label: on },
      ],
      () => s.assists[key],
      (x) => {
        s.assists[key] = x;
        s.assistPreset = presetOf(s.assists);
        ch();
        refreshAll(d);
      },
    );
  assist('Gearbox', 'autoGearbox', 'AUTOMATIC', 'MANUAL');
  assist('ABS', 'abs');
  assist('Traction control', 'tractionControl');
  assist('Counter-steer help', 'steeringHelp');
  assist('Extra steering smoothing', 'steeringSmoothing');
  slider(d, 'Gamepad steering dead zone', 0, 0.3, 0.01, () => s.pad.deadZone, (x) => ((s.pad.deadZone = x), ch()), pct);
  slider(d, 'Gamepad steering linearity', 1, 2.5, 0.1, () => s.pad.linearity, (x) => ((s.pad.linearity = x), ch()), (x) => x.toFixed(1));
  toggle(d, 'Gamepad rumble', () => s.pad.rumble, (x) => ((s.pad.rumble = x), ch()));

  // Input: keyboard remapping and controller / wheel calibration.
  inputPage(page(), ctx);

  // HUD.
  const u = page();
  const hud = (label: string, key: keyof Settings['hud']) => toggle(u, label, () => s.hud[key], (x) => ((s.hud[key] = x), ch()));
  hud('Speed', 'speed');
  hud('Revs, shift lights and gear', 'revs');
  hud('Stage time', 'time');
  hud('Split times', 'splits');
  hud('Stage progress', 'progress');
  hud('Penalty notices', 'penalties');
  hud('G-force meter', 'gforce');
  hud('Speed, revs and G-meter in the cockpit view', 'inCockpit');
  toggle(u, 'Ghost of your best run', () => s.ghost, (x) => ((s.ghost = x), ch()));

  bar.select(0);
  const back = h('div', 'btn-row', undefined, el);
  button('BACK', () => screens.pop(), '', back);
  return { el, initial: bar.el };
}

const KEY_ACTIONS: [Action, string][] = [
  ['throttle', 'Throttle'],
  ['brake', 'Brake / reverse'],
  ['steerLeft', 'Steer left'],
  ['steerRight', 'Steer right'],
  ['handbrake', 'Handbrake'],
  ['shiftUp', 'Shift up'],
  ['shiftDown', 'Shift down'],
  ['camera', 'Camera'],
  ['lookLeft', 'Look left'],
  ['lookRight', 'Look right'],
  ['lookBack', 'Look back'],
  ['reset', 'Reset to road'],
  ['lights', 'Lights'],
  ['horn', 'Horn'],
  ['pause', 'Pause'],
];

type PadSlot = 'throttle' | 'brake' | 'handbrake' | 'shiftUp' | 'shiftDown' | 'camera' | 'lookBack' | 'reset' | 'pause';
const PAD_ACTIONS: [PadSlot | 'steer', string, CaptureKind, string][] = [
  ['steer', 'Steering', 'steer', 'Turn the wheel or stick fully left and fully right'],
  ['throttle', 'Throttle', 'pedal', 'Press the throttle pedal or trigger all the way'],
  ['brake', 'Brake', 'pedal', 'Press the brake pedal or trigger all the way'],
  ['handbrake', 'Handbrake', 'pedal', 'Pull the handbrake or press its button'],
  ['shiftUp', 'Shift up', 'button', 'Press the shift-up paddle or button'],
  ['shiftDown', 'Shift down', 'button', 'Press the shift-down paddle or button'],
  ['camera', 'Camera', 'button', 'Press a button'],
  ['lookBack', 'Look back', 'button', 'Press a button'],
  ['reset', 'Reset to road', 'button', 'Press a button'],
  ['pause', 'Pause', 'button', 'Press a button'],
];

function steerLabel(m: PadMapping): string {
  return m.steerMin !== undefined ? `AXIS ${m.steerAxis} (CALIBRATED)` : `AXIS ${m.steerAxis}`;
}

function inputPage(p: HTMLElement, ctx: MenuContext): void {
  const s = ctx.settings;
  h('div', 'row-note', 'Keyboard: select an action, press Enter, then the new key (Esc cancels).', p);
  const keyRows: (() => void)[] = [];
  for (const [action, label] of KEY_ACTIONS) {
    const row = h('div', 'row', undefined, p);
    h('div', 'row-label', label, row);
    const b = h('button', 'ctl', undefined, row);
    b.type = 'button';
    b.dataset.nav = '';
    b.dataset.action = action;
    const render = () => (b.textContent = s.keys[action].map(keyLabel).join(' · '));
    keyRows.push(render);
    render();
    b.addEventListener('click', () => {
      b.textContent = 'PRESS A KEY…';
      b.classList.add('capturing');
      ctx.captureKey((code) => {
        b.classList.remove('capturing');
        if (code) {
          rebindKey(s.keys, action, code);
          ctx.changed();
        }
        keyRows.forEach((r) => r());
        b.focus();
      });
    });
  }
  const kr = h('div', 'btn-row', undefined, p);
  button('RESET KEYS', () => {
    s.keys = structuredClone(DEFAULT_KEYS);
    ctx.changed();
    keyRows.forEach((r) => r());
  }, 'btn-small', kr);

  h('div', 'row-note', 'Gamepad, steering wheel and pedals: select a control, press Enter (or A), then move it. Pedals and wheels are calibrated end to end; works with any controller the browser exposes.', p);
  const padRows: (() => void)[] = [];
  for (const [slot, label, kind, prompt] of PAD_ACTIONS) {
    const row = h('div', 'row', undefined, p);
    h('div', 'row-label', label, row);
    const b = h('button', 'ctl', undefined, row);
    b.type = 'button';
    b.dataset.nav = '';
    b.dataset.pad = slot;
    const render = () => (b.textContent = slot === 'steer' ? steerLabel(s.padMap) : bindingLabel(s.padMap[slot] as PadBinding));
    padRows.push(render);
    render();
    b.addEventListener('click', () => {
      b.textContent = prompt.toUpperCase() + '…';
      b.classList.add('capturing');
      ctx.capturePad(kind, (r) => {
        b.classList.remove('capturing');
        if (r && r.kind === 'steer' && slot === 'steer') {
          s.padMap.steerAxis = r.axis;
          s.padMap.steerMin = r.min;
          s.padMap.steerCenter = r.center;
          s.padMap.steerMax = r.max;
          ctx.changed();
        } else if (r && r.kind !== 'steer' && slot !== 'steer') {
          s.padMap[slot] = r.binding;
          if (slot === 'throttle') s.padMap.invertThrottle = false;
          if (slot === 'brake') s.padMap.invertBrake = false;
          ctx.changed();
        }
        padRows.forEach((x) => x());
        if (!r) b.textContent = 'NOTHING DETECTED — TRY AGAIN';
        b.focus();
      });
    });
  }
  const pr = h('div', 'btn-row', undefined, p);
  button('RESET CONTROLLER', () => {
    s.padMap = structuredClone(STANDARD_PAD);
    ctx.changed();
    padRows.forEach((r) => r());
  }, 'btn-small', pr);
}

function assistPresetStepper(parent: HTMLElement, ctx: MenuContext, after: () => void): HTMLElement {
  const s = ctx.settings;
  return stepper<AssistPreset>(
    parent,
    'Assists',
    [
      { value: 'rookie', label: 'ROOKIE' },
      { value: 'realistic', label: 'REALISTIC' },
      { value: 'custom', label: 'CUSTOM' },
    ],
    () => s.assistPreset,
    (v) => {
      // "Custom" is reached by changing single assists; stepping onto it skips to the next preset.
      const next: AssistPreset = v === 'custom' ? (s.assistPreset === 'rookie' ? 'realistic' : 'rookie') : v;
      s.assistPreset = next;
      s.assists = assistsFor(next, s.assists);
      ctx.changed();
      after();
    },
  );
}


const PAD: [string, string][] = [
  ['LEFT STICK', 'Steer'],
  ['RT', 'Throttle'],
  ['LT', 'Brake · reverse when stopped'],
  ['A / ✕', 'Handbrake'],
  ['RB / LB', 'Shift up / down (manual gears)'],
  ['Y / △', 'Change camera'],
  ['X / □', 'Look back'],
  ['RIGHT STICK', 'Look around'],
  ['VIEW / SHARE', 'Reset to road'],
  ['MENU / OPTIONS', 'Pause'],
];

/** Keyboard rows of the controls card with the player's current bindings. */
function keyRows(ctx: MenuContext): [string, string][] {
  const k = ctx.settings.keys;
  const l = (a: Action) => k[a].map(keyLabel).join(' / ');
  return [
    [l('throttle'), 'Throttle'],
    [l('brake'), 'Brake · reverse when stopped'],
    [`${l('steerLeft')}  ${l('steerRight')}`, 'Steer'],
    [l('handbrake'), 'Handbrake'],
    [`${l('shiftUp')} / ${l('shiftDown')}`, 'Shift up / down (manual gears)'],
    [l('camera'), 'Change camera'],
    [`${l('lookLeft')} / ${l('lookRight')}`, 'Look left / right (hold)'],
    [l('lookBack'), 'Look back (hold)'],
    ['RIGHT MOUSE', 'Look around (hold)'],
    [l('reset'), `Reset to road (+${RESET_PENALTY} s)`],
    [`${l('lights')} / ${l('horn')}`, 'Lights / horn'],
    [l('pause'), 'Pause'],
  ];
}

export function controlsScreen(ctx: MenuContext, screens: Screens, cta: string, onDone: () => void): Screen {
  const el = h('div', 'menu-panel controls');
  const head = h('div', 'panel-head', undefined, el);
  h('h1', '', 'CONTROLS', head);
  h('div', 'panel-sub', 'Drive on the gravel with the throttle as much as the wheel. Listen to the co-driver.', head);
  const cols = h('div', 'controls-cols', undefined, el);
  for (const [title, list] of [
    ['KEYBOARD', keyRows(ctx)],
    ['GAMEPAD', PAD],
  ] as const) {
    const col = h('div', 'controls-col', undefined, cols);
    h('div', 'card-kicker', title, col);
    for (const [k, a] of list) {
      const r = h('div', 'key-row', undefined, col);
      h('span', 'keycap', k, r);
      h('span', 'key-action', a, r);
    }
  }
  const assists = h('div', 'controls-assists', undefined, el);
  assistPresetStepper(assists, ctx, () => {
    note.textContent = assistNote(ctx.settings.assistPreset);
  });
  const note = h('div', 'row-note', assistNote(ctx.settings.assistPreset), assists);
  const row = h('div', 'btn-row', undefined, el);
  const go = button(cta, () => {
    ctx.settings.controlsSeen = true;
    ctx.changed();
    onDone();
  }, 'btn-primary', row);
  go.id = 'btn-controls-done';
  return { el, initial: go, onBack: () => onDone() };
}

function assistNote(p: AssistPreset): string {
  if (p === 'rookie') return 'Rookie: automatic gears, counter-steer help, ABS and light traction control.';
  if (p === 'realistic') return 'Realistic: no ABS, no traction control, no steering help — like the real car.';
  return 'Custom: your own mix of assists (Settings → Driving).';
}

interface ManifestEntry {
  title: string;
  author: string;
  source: string;
  licence: string;
}

export function creditsScreen(screens: Screens): Screen {
  const el = h('div', 'menu-panel credits');
  const head = h('div', 'panel-head', undefined, el);
  h('h1', '', 'CREDITS', head);
  h('div', 'panel-sub', 'Every third-party and generated asset in this game, with its source and licence.', head);
  const body = h('div', 'panel-body credits-body', undefined, el);
  body.tabIndex = 0;
  body.dataset.nav = '';
  h('div', 'credit-callout', 'Powered by Poly Haven — CC0 textures and plant scans from polyhaven.com', body);
  h('div', 'credit-callout', 'Co-driver voice and sound effects generated with ElevenLabs', body);
  for (const a of (manifest as { assets: ManifestEntry[] }).assets) {
    const r = h('div', 'credit', undefined, body);
    h('div', 'credit-title', a.title, r);
    h('div', 'credit-line', `${a.author} · ${a.licence}`, r);
    h('div', 'credit-src', a.source, r);
  }
  for (const [t, l] of [
    ['three.js', 'MIT licence · threejs.org'],
    ['Rapier physics (@dimforge/rapier3d-compat)', 'Apache 2.0 licence · rapier.rs'],
    ['Barlow Condensed typeface', 'SIL Open Font License 1.1 · Jeremy Tribby'],
  ]) {
    const r = h('div', 'credit', undefined, body);
    h('div', 'credit-title', t, r);
    h('div', 'credit-line', l, r);
  }
  h('div', 'credit-line', 'Car, stage, livery, team and location are fictional. Built with Claude Code.', body);
  const row = h('div', 'btn-row', undefined, el);
  const back = button('BACK', () => screens.pop(), '', row);
  return { el, initial: back };
}

export function pauseScreen(ctx: MenuContext, screens: Screens): Screen {
  const el = h('div', 'menu-panel pause');
  const head = h('div', 'panel-head', undefined, el);
  h('h1', '', 'PAUSED', head);
  h('div', 'panel-sub', `${ctx.stageName} · stage time ${ctx.stageTime()}`, head);
  const nav = h('div', 'menu-nav', undefined, el);
  const resume = button('RESUME', () => ctx.resume(), 'btn-primary', nav);
  resume.id = 'btn-resume';
  button('RESTART STAGE', () => ctx.restart(), '', nav);
  button(`RESET TO ROAD (+${RESET_PENALTY} S)`, () => ctx.resetToRoad(), '', nav);
  button('SETTINGS', () => screens.push(settingsScreen(ctx, screens)), '', nav);
  button('CONTROLS', () => screens.push(controlsScreen(ctx, screens, 'BACK', () => screens.pop())), '', nav);
  button('QUIT TO MENU', () => ctx.quitToMenu(), '', nav);
  fullscreenButton(ctx, el);
  return { el, initial: resume, onBack: () => ctx.resume(), onStart: () => ctx.resume() };
}

export function finishScreen(ctx: MenuContext, r: StageResult, prevBest: BestEntry | null, newBest: boolean, penaltyLines: string[]): Screen {
  const el = h('div', 'menu-panel finish');
  const head = h('div', 'panel-head', undefined, el);
  h('div', 'card-kicker', ctx.stageName.toUpperCase(), head);
  h('h1', '', 'STAGE COMPLETE', head);
  const big = h('div', 'finish-time', formatTime(r.total), el);
  big.id = 'finish-time';
  if (newBest) h('div', 'finish-badge', prevBest ? `NEW PERSONAL BEST  ${formatDelta(r.total - prevBest.total)}` : 'FIRST TIME SET — PERSONAL BEST', el);
  else if (prevBest) h('div', 'finish-sub', `Personal best ${formatTime(prevBest.total)}  (${formatDelta(r.total - prevBest.total)})`, el);
  const table = h('div', 'finish-table', undefined, el);
  const line = (label: string, value: string, delta?: number | null) => {
    const row = h('div', 'finish-row', undefined, table);
    h('span', 'finish-label', label, row);
    h('span', 'finish-value', value, row);
    const d = h('span', 'finish-delta', delta === undefined || delta === null ? '' : formatDelta(delta), row);
    if (delta !== undefined && delta !== null) d.classList.add(delta <= 0 ? 'good' : 'bad');
  };
  r.splits.forEach((t, i) => line(`Split ${i + 1}`, formatTime(t), prevBest ? t - prevBest.splits[i] : null));
  line('Flying finish', formatTime(r.time), prevBest ? r.time - prevBest.time : null);
  line('Penalties', r.penalties > 0 ? `+${r.penalties.toFixed(0)} s` : 'none');
  for (const p of penaltyLines) h('div', 'finish-pen', p, table);
  line('Stage time', formatTime(r.total), prevBest ? r.total - prevBest.total : null);
  const row = h('div', 'btn-row', undefined, el);
  const again = button('RESTART STAGE', () => ctx.restart(), 'btn-primary', row);
  again.id = 'btn-restart';
  button('MENU', () => ctx.quitToMenu(), '', row);
  return { el, initial: again, onBack: () => ctx.quitToMenu() };
}

export function touchNotice(onOk: () => void): Screen {
  const el = h('div', 'menu-panel notice');
  const head = h('div', 'panel-head', undefined, el);
  h('h1', '', 'TOUCH CONTROLS', head);
  h('p', 'notice-text', 'Hold your phone sideways. Drag your left thumb anywhere on the left half to steer; GAS, BRAKE (reverse when stopped) and HAND are on the right. RESET, CAM and pause are at the top. A keyboard or a gamepad works too, and suits the stage best.', el);
  const row = h('div', 'btn-row', undefined, el);
  const ok = button('CONTINUE', onOk, 'btn-primary', row);
  return { el, initial: ok, onBack: onOk };
}

/** Short-lived message in the corner (controller connected, etc.). */
export function toast(parent: HTMLElement, text: string): void {
  const t = h('div', 'toast', text, parent);
  setTimeout(() => t.classList.add('out'), 3500);
  setTimeout(() => t.remove(), 4000);
}
